/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';

/* COMPONENTS */
import Select, { components } from 'react-select';
import { Cross2Icon, ChevronDownIcon } from '@radix-ui/react-icons';

/* UTILS */
import i18n from '@/utils/i18n.utils';
import { Label } from '@radix-ui/react-label';
import { cn } from '@/lib/utils.ts';
import Image from '../ui/Image/Image';
import { Option } from '../Select/Select.component';

/* PROPS */
interface SelectComponentProps {
  value: string | undefined;
  label?: string;
  placeholder?: string;
  options: Option[];
  handleChange: (value: string) => void;
  width?: string;
  error?: string;
  className?: string;
  dropdownIndicator?: boolean;
  clearIndicator?: boolean;
  noOptionsMessage?: string;
  disabled?: boolean;
  required?: boolean;
  startsWith?: boolean;
  labelClassName?: string;
}

export function SelectSearchComponent({
  value,
  label,
  placeholder = i18n.t('activation.selectPlaceholder'),
  options,
  handleChange,
  width,
  error,
  className,
  dropdownIndicator = false,
  clearIndicator = true,
  noOptionsMessage,
  disabled = false,
  required = false,
  startsWith = true,
  labelClassName,
}: SelectComponentProps) {
  const [selectedValue, setSelectedValue] = useState<string | undefined>(value);
  const [selectOptions, setSelectOptions] = useState<Option[]>(
    options.slice(0, 20)
  );

  useEffect(() => {
    if (value) {
      setSelectedValue(value);
    }
  }, [value])

  const filterOptionsBySearch = (search: string) => {
    const normalizeString = (str: string) =>
      str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const filteredOptions = options
      .filter(option => {
        const labelMatch = normalizeString(option.label).toLowerCase();
        const valueMatch = normalizeString(option.value).toLowerCase();
        const searchTerm = normalizeString(search).toLowerCase();

        return startsWith
          ? labelMatch.startsWith(searchTerm) || valueMatch.startsWith(searchTerm)
          : labelMatch.includes(searchTerm) || valueMatch.includes(searchTerm);
      })
      .slice(0, 20);
    setSelectOptions(filteredOptions);
  };

  const onSelectChange = (value: string) => {
    setSelectedValue(value);
    handleChange(value);
  };

  const selectedOption = options.find(option => option.value === selectedValue);

  return (
    <div style={{ width }} className={cn('flex flex-col !h-[52px] ', className)}>
      {label && (
        <Label className={cn(labelClassName ?? 'text-bodyLarge', 'pb-3')} htmlFor="select">
          {label}
        </Label>
      )}

      <Select
        onInputChange={inputValue => {
          filterOptionsBySearch(inputValue);
        }}
        onFocus={() => {
          filterOptionsBySearch('');
        }}
        options={selectOptions}
        value={selectedOption}
        onChange={option => onSelectChange(option?.value || '')}
        placeholder={`${placeholder}${required ? '*' : ''}`}
        isClearable
        isDisabled={disabled}
        noOptionsMessage={() =>
          noOptionsMessage ?? i18n.t('inputs.countries.noOptions')
        }
        components={{
          DropdownIndicator: props =>
            dropdownIndicator ? (
              <components.DropdownIndicator {...props}>
                <ChevronDownIcon className="h-4 w-4 m-3 text-gray-500" />
              </components.DropdownIndicator>
            ) : null,
          IndicatorSeparator: _ => null,
          ClearIndicator: props =>
            clearIndicator ? (
              <components.ClearIndicator {...props}>
                <Cross2Icon className="h-4 w-4 m-3 text-gray-500" />
              </components.ClearIndicator>
            ) : null,
          Option: props => (
            <components.Option {...props}>
              <div className="flex items-center">
                {props.data.icon && (
                  <Image
                    src={props.data.icon}
                    alt={`${props.data.label}-icon`}
                    className="mr-2 h-5 w-5 rounded-full"
                  />
                )}
                {props.data.label}
              </div>
            </components.Option>
          ),
          SingleValue: props => (
            <components.SingleValue {...props}>
              <div className="flex items-center">
                {props.data.icon && (
                  <Image
                    src={props.data.icon}
                    alt={`${props.data.label}-icon`}
                    className="mr-2 h-5 w-5 rounded-full"
                  />
                )}
                {props.data.label}
              </div>
            </components.SingleValue>
          ),
        }}
        theme={theme => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: 'black',
          },
        })}
        styles={{
          placeholder: styles => ({
            ...styles,
            color: '#7C7C7C',
            fontSize: '13px',
          }),
          control: (styles, { isFocused }) => ({
            ...styles,
            backgroundColor: '#F7F7F7',
            borderWidth: isFocused ? 1 : 0,
            boxShadow: 'none',
            height: 52,
            opacity: disabled ? 0.5 : 1,
            border: '0.5px solid rgba(0, 0, 0, 0.05)'
          }),
          option: (styles, { isSelected }) => ({
            ...styles,
            backgroundColor: isSelected ? '#F7F7F7' : '#FFFFFF',
            color: 'black',
            ':hover': {
              backgroundColor: '#F7F7F7',
            },
          }),
          valueContainer: styles => ({
            ...styles,
            padding: '0 16px',
          }),
          singleValue: styles => ({
            ...styles,
            color: 'black',
          }),
        }}
      />
      {error && <p className="text-red-500 text-bodyMedium">{error}</p>}
    </div>
  );
}
